import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import { LogIn, Mail, Key, ArrowLeft, UserPlus } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function AgentLogin() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    agent_code: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/agents/login`, formData);
      
      // Store token and agent data
      localStorage.setItem('token', response.data.access_token);
      localStorage.setItem('agent', JSON.stringify(response.data.agent));
      
      toast.success('Login successful!');
      navigate('/agent/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        {/* Header */}
        <div className="text-center mb-8">
          <img src="/hakicheck-logo.png" alt="HakiCheck" className="h-16 mx-auto mb-4" />
          <h1 className="text-4xl font-bold text-gray-900 mb-2">
            Field Agent Login
          </h1>
          <p className="text-gray-600">
            Access your agent dashboard
          </p>
        </div>

        {/* Login Form */}
        <div className="bg-white rounded-2xl shadow-xl p-8">
          <div className="flex items-center gap-2 mb-6">
            <LogIn className="text-[#5a738f]" size={24} />
            <h2 className="text-2xl font-bold text-gray-900">Agent Login</h2>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Email */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Mail size={16} className="inline mr-2" />
                Email Address
              </label>
              <input
                type="email"
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#5a738f] focus:border-transparent"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                placeholder="your.email@example.com"
              />
            </div>

            {/* Agent Code */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Key size={16} className="inline mr-2" />
                Agent Code
              </label>
              <input
                type="text"
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#5a738f] focus:border-transparent font-mono text-lg tracking-wider"
                value={formData.agent_code}
                onChange={(e) => setFormData({...formData, agent_code: e.target.value.toUpperCase()})}
                placeholder="AG-XXXX"
                maxLength={7}
              />
              <p className="text-sm text-gray-500 mt-2">
                Enter the agent code you received during registration
              </p>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-[#5a738f] hover:bg-[#4a5f7f] text-white font-semibold py-4 px-6 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Logging in...' : 'Login to Dashboard'}
            </button>
          </form>

          {/* Footer Links */}
          <div className="mt-6 pt-6 border-t border-gray-200 space-y-3">
            <button
              onClick={() => navigate('/agent/register')}
              className="w-full flex items-center justify-center gap-2 text-[#5a738f] hover:text-[#4a5f7f] font-medium py-2"
            >
              <UserPlus size={18} />
              Don't have an agent code? Register here
            </button>
            <button
              onClick={() => navigate('/')}
              className="w-full text-gray-600 hover:text-gray-800 text-sm inline-flex items-center justify-center gap-2 py-2"
            >
              <ArrowLeft size={16} />
              Back to Home
            </button>
          </div>
        </div>

        {/* Info Box */}
        <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-800">
          <p className="font-semibold mb-1">New to field agent work?</p>
          <p>Register as a field agent to start receiving verification jobs in your area.</p>
        </div>
      </div>
    </div>
  );
}

export default AgentLogin;
